import {marked} from 'marked';
import type {ReactElement} from 'react';
import {useRef, useLayoutEffect, useState, memo} from 'react';
import {createPortal} from 'react-dom';
import styles from './markdown-renderer.module.scss';
import DOMPurify from 'dompurify';
import {attachSuffixContainer, deindentHtmlLines} from '@Components/ai-chat-window/components/markdown-renderer/markdown-renderer.library';
import type {MarkdownRendererProps} from '@Components/ai-chat-window/components/markdown-renderer/markdown-renderer.types';

function MarkdownRenderer({content, suffix}: MarkdownRendererProps): ReactElement {
  const contentRef = useRef<HTMLDivElement>(null);
  const [suffixContainer, setSuffixContainer] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const body = contentRef.current?.firstElementChild;

    if (!body || !suffix) {
      setSuffixContainer(null);

      return;
    }

    setSuffixContainer(attachSuffixContainer(body));
  }, [content, suffix]);

  const sanitizeConfig = {
    USE_PROFILES: {html: true},
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
  };

  const renderBody = (): ReactElement => {
    const rawHtml = marked.parse(deindentHtmlLines(content), {async: false});
    return <div dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(rawHtml, sanitizeConfig)}} />;
  };

  return (
    <div ref={contentRef} className={styles.content}>
      {renderBody()}

      {suffixContainer && suffix ? createPortal(suffix, suffixContainer) : null}
    </div>
  );
}

export default memo(MarkdownRenderer);
